/**
 * Admin design tokens — the single source of truth for the redesign in code.
 *
 * Values come from docs/design/admin-ui/design-system.md §1-§5, which outranks
 * any ticket description: CAE-1537's own description omitted the `featuredEvent`
 * size, `cardTitle`'s letter-spacing, and all four status-pill palettes.
 *
 * Where that document and the handoff's own `shared/modals.css` disagree, the
 * stylesheet wins — it is what design signed off visually, and §1 itself points
 * at it ("tokens ... defined as CSS variables on :root in shared/modals.css.
 * Reuse exact tokens"). That applies to `accentSoft`, `statusPill` and the h1
 * line-height; each carries a comment saying so. The remaining divergences are
 * listed in theme/README.md and are worth raising on CAE-1536.
 *
 * Both consumers are derived from this object:
 *   - `buildCssVariables()` emits the `:root` custom properties (css-variables.ts)
 *   - `adminTheme` mirrors them into MUI (theme.ts)
 *
 * Deriving both from one object is what keeps the CSS and MUI values in step;
 * `tokens.test.ts` asserts the parity so it cannot silently drift.
 */
import type { ContestLifecycleState } from '../contests/lifecycle';

/** Weights loaded from Google Fonts in index.html. Keep the two in sync. */
export const SANS_STACK = "'Geist', 'Segoe UI', 'Helvetica', 'Arial', sans-serif";
export const MONO_STACK = "'Geist Mono', 'SFMono-Regular', 'Menlo', 'Consolas', monospace";

/**
 * Lighten a hex colour towards white by `amount` of the remaining headroom:
 * per channel `c + (255 - c) * amount`, rounded.
 *
 * §1 describes `--accent-soft` this way (at 0.88) and the ticket asks for it as
 * a helper "so a future accent change stays consistent" — which is what this is
 * for. It is NOT how the current value is produced: see ACCENT_SOFT below.
 */
export const softenHex = (hex: string, amount: number): string => {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match?.[1]) {
    throw new Error(`softenHex expects a #rrggbb colour, received "${hex}"`);
  }

  const channels = [0, 2, 4].map((offset) => {
    const channel = Number.parseInt(match[1]!.slice(offset, offset + 2), 16);
    const softened = Math.round(channel + (255 - channel) * amount);
    return Math.min(255, Math.max(0, softened)).toString(16).padStart(2, '0');
  });

  return `#${channels.join('')}`;
};

const ACCENT = '#2563eb';
const INK_3 = '#6b6f7a';

/**
 * The grey a filled thing sits in front of — the unrun part of the readiness
 * ring, and the trough the segmented tabs' selected pill slides over. One
 * literal in the prototype, used by both rules, so one constant here.
 */
const TRACK_NEUTRAL = '#eaecf0';

/**
 * A half-step lighter than TRACK_NEUTRAL, and the prototype's most reused
 * neutral: the draft pill's ground, the trough of the prize-delivery bar, the
 * milestone rail, and the count badge on a selected tab.
 */
const NEUTRAL_SOFT = '#eef0f4';

/**
 * The deep green and deep amber the soft washes carry text in. `--success` and
 * `--warn` themselves are the glyph colours and sit too light on their own
 * backgrounds for a label; the prototype reaches for these instead, in the live
 * pill, the market badge and the tab attention badge.
 */
const SUCCESS_STRONG = '#0b6635';
const WARN_STRONG = '#8a4a08';

/** The ink NEUTRAL_SOFT carries — the draft pill's label, the selected tab's badge. */
const INK_ON_NEUTRAL_SOFT = '#3a3d46';

/**
 * The grey of something switched off or not yet reached — an unchecked toggle's
 * track, and the outline of a milestone step still in the future.
 */
const MUTED_EDGE = '#d4d6dd';

/**
 * `.m-pill`'s own geometry. Named so `statusPill` and `statusPillSizes.md` can be
 * the same object rather than two literals that could drift.
 */
const STATUS_PILL_MD: StatusPillGeometry = {
  padding: '3px 10px 3px 8px',
  gap: 7,
  dotSize: 6,
  labelSize: '12px',
  labelWeight: 600,
  labelTracking: '0.01em',
};

/** How much `--accent-soft` is lightened towards white (spec §1). */
export const SOFT_BACKGROUND = 0.88;

/**
 * The design's own value, taken from `:root` in the handoff's `shared/modals.css`.
 *
 * §1 calls `--accent-soft` "derived" and gives `c + (255 - c) * 0.88`, then adds
 * "implement as a derived value **or precompute**" — but the precomputed value the
 * design actually ships is hand-tuned and the formula does not reproduce it:
 * 0.88 yields #e5ecfd, and no mix of #2563eb with white yields a `ff` blue channel.
 * §1's own preamble points at modals.css as where the tokens live and says to reuse
 * exact tokens, so the shipped value wins over the approximation that describes it.
 *
 * `softenHex` is kept for a future accent change, per the ticket. If the accent
 * moves, re-derive from it and have design confirm the result.
 */
const ACCENT_SOFT = '#e8efff';

/** `--accent-strong`, and the scheduled pill's text — one literal, both uses. */
const ACCENT_STRONG = '#173cb4';

export interface ColorTokens {
  bg: string;
  surface: string;
  surfaceMuted: string;
  line: string;
  lineStrong: string;
  ink: string;
  ink2: string;
  ink3: string;
  ink4: string;
  accent: string;
  accentSoft: string;
  /**
   * `--accent-strong`. In the prototype's `:root` but missing from §1's table,
   * so CAE-1537 had no way to know about it. The accent at reading weight —
   * modal titles, the selected outcome's name, the queued-sync badge.
   */
  accentStrong: string;
  success: string;
  successSoft: string;
  warn: string;
  warnSoft: string;
  danger: string;
  /**
   * `#fce8e6` — the danger wash. Missing from §1's table and from the
   * prototype's own `:root`, which is an omission rather than a decision: every
   * other state in the ramp has its soft, and the literal appears seven times
   * across `modals.css` doing exactly this job (the destructive button and icon
   * button hovers, the danger menu item, the FREE_SPINS type pill, the prize
   * JSON error ring, and Basic Info's "Required" field tag).
   */
  dangerSoft: string;
}

/**
 * Named neutrals the shared primitives (CAE-1539) need that §1's ramp does not
 * carry. Every one is lifted from `docs/design/admin-ui/prototype/*.css`.
 *
 * They live in their own group rather than in `ColorTokens` because §1 is the
 * published design system — the palette design signs off and screens compose
 * from — while these are private to the kit: the grey behind an ID chip's label
 * box, the hover a checklist row takes. Folding them into `color` would grow the
 * `:root` surface with names no screen should reach for.
 *
 * Which is also why these get no CSS custom property. The §1 ramp is reachable
 * both ways because plain CSS and Tailwind inside the app consume it; the kit's
 * own colours are only ever read through `sx`, so a variable would be a second
 * surface with nothing on the other end of it. `ui/no-hard-coded-colour.test.ts`
 * is what holds the line that these live here rather than inline.
 */
export interface ComponentColorTokens {
  /** `.id-chip .id-k` — the grey label box of the two-tone ID chip. */
  idChipKeyBg: string;
  /**
   * `.head-meta .sep` — the 3px dot that separates the Contest Overview
   * header's age chip from its event date. A notch darker than `line`, because
   * it reads as a mark in its own right rather than as a rule between things.
   */
  metaSeparator: string;
  /**
   * `.m-outcome.is-sel` — the hairline drawn *inside* a picked outcome row in
   * the Add Outcomes modal (CAE-1553), over its `accentSoft` ground.
   *
   * The same `#c7d4f5` the scheduled status pill borders with, named separately
   * because the two are unrelated: one edges a pill, the other inlays a row,
   * and a future change to either must not silently move the other.
   */
  outcomeSelectedInset: string;
  /**
   * `#f3c7c0` — the edge that pairs with `dangerSoft`, the same relationship
   * `marketBadgeEmptyBorder` has to `warnSoft`. Borders the "Required" tag on
   * Edit › Basic Info, the fixture detach button and the danger icon button.
   */
  dangerSoftBorder: string;
  /**
   * `#e9a59c` — the same edge one step deeper, for `.fx-detach-btn:hover`.
   *
   * The ribbon's detach button is danger-coloured at rest, so its hover cannot
   * announce itself by *becoming* red the way the destructive buttons elsewhere
   * do. It deepens instead, and the border is where that reads.
   */
  dangerSoftBorderHover: string;
  /** `.progress-ring .track` — the unfilled remainder of the readiness ring. */
  ringTrack: string;
  /** `.checkitem:hover` — the wash an inset row takes under the pointer. */
  rowHover: string;
  /**
   * `.ready-banner` — the green the Publish readiness card's success line reads
   * in, over `--success-soft`.
   *
   * Darker than `--success`, which is the glyph colour and fails contrast as
   * body text on its own soft background. Its own value rather than
   * `SUCCESS_STRONG`: the stylesheet sets this one literally, and the two are a
   * shade apart.
   */
  readyBannerText: string;
  /** `.bar` — the trough a KPI progress bar fills, and the milestone rail. */
  barTrack: string;
  /** `.m-marketsbadge-ok` — the green a delivered market count reads in. */
  marketBadgeOkText: string;
  /** The muted half of that badge, carrying the outcome count. */
  marketBadgeOkSubText: string;
  /** `.m-marketsbadge-empty` — amber for a fixture the feed gave nothing for. */
  marketBadgeEmptyText: string;
  marketBadgeEmptyBorder: string;
  /** `.tab-count` — the neutral badge on an unselected tab. */
  countBadgeBg: string;
  countBadgeText: string;
  /** The same badge on the selected tab, lifted against its white pill. */
  countBadgeSelectedBg: string;
  countBadgeSelectedText: string;
  /** `.tab-count[data-attn]` — warm amber for a section still to configure. */
  countBadgeAttnBg: string;
  countBadgeAttnText: string;
  /**
   * `.sr-cl-empty` — the hollow ring a Setup Rail step wears before it is done.
   *
   * Its own grey, a shade firmer than `toggleTrackOff`: this is a 1.5px ring on
   * white rather than a filled track, and the lighter value disappears at that
   * weight.
   */
  setupRailIdleRing: string;
  /** `.m-toggle-track` unchecked, and the milestone track's future dots. */
  toggleTrackOff: string;
  /** `.fixture:hover` — a shade deeper than `rowHover`, over `--surface-muted`. */
  fixtureRowHover: string;
  /**
   * `.vw-row:hover` — the border the read-only sections' rows firm up to.
   *
   * The same value as `statusChipHoverBorder` and `metaSeparator`, named
   * separately because they are three unrelated decisions that happen to agree
   * today: a row's hover edge, a chip's hover edge and a drawn separator dot.
   */
  viewRowHoverBorder: string;
  /**
   * `.admin-empty-art` — the rounded tile an empty state's line-art icon sits
   * in. A half-step off `--surface-muted`, which the same empty state uses for
   * its hint footer, so the two do not flatten into one another.
   */
  emptyArtBg: string;
  /**
   * `.m-modal-host-backdrop` — the ink wash behind an open modal. The README
   * says `rgba(13,14,18,.42)`; modals.css draws `rgba(12,13,16,.40)`, which is
   * `--ink` at 40%, and the stylesheet wins.
   */
  modalScrim: string;
  /** `.m-dot-sep` — the 3px dot between a modal foot's summary facts. */
  dotSeparator: string;
  /**
   * `.badge` on a fixture row — a smaller, dotless cousin of the status pill,
   * with its own palette and its own three states. Not `status`: a fixture's
   * `live` here is a flatter green than the pill's, and `upcoming` has no pill
   * equivalent at all.
   */
  fixtureBadge: Record<'upcoming' | 'live' | 'finished', { bg: string; text: string }>;
  /**
   * `.cl-statuschip.is-active` — the selected filter chip, which is **ink, not
   * accent** (CAE-1542).
   *
   * Worth stating plainly because both the ticket and `01-contests-list`'s prose
   * describe an accent chip: accent border, accent text, `--accent-soft` ground.
   * The stylesheet fills it `#0c0d10` with white text, and
   * `screenshots/01-default.png` renders it that way. The standing rule is that
   * the stylesheet wins; see docs/design/admin-ui/prototype/README.md.
   *
   * The ground itself is `color.ink`, so only the count badge sitting on it
   * needs a token of its own — a white veil rather than a flat grey, because it
   * has to read against ink here and against white everywhere else.
   */
  statusChipActiveCountBg: string;
  /** `.cl-statuschip-count` at rest — the same neutral the tab badges wear. */
  statusChipCountBg: string;
  statusChipCountText: string;
  /** `.cl-statuschip:hover` — a half-step darker than `--line-strong`. */
  statusChipHoverBorder: string;
  /**
   * `.cl-pag-btn:hover` and `.cl-pag-rowsel select:hover` — a darker edge again
   * than the chip's. Two rules, one literal, so one token.
   */
  controlHoverBorder: string;
  /**
   * `.cl-id` — the flat monospace mini-pill a contest id wears **in a list row**.
   *
   * Not the `IdChip` primitive, despite the ticket naming it: that is the
   * two-tone `ID | CT-58291` chip from the Contest Overview. A table row draws a
   * single grey pill holding the id alone.
   */
  listIdPillBg: string;
  /**
   * `.cl-pausebtn.is-paused:hover` — the resume button's hover ground
   * (CAE-1543).
   *
   * One step darker than `successSoft`, which is the button's resting
   * background. Resume is the only control in the kit whose *resting* state is
   * already tinted, so it is the only one that needs a second shade to move to
   * on hover; everything else hovers from transparent onto `bg`.
   */
  rowResumeHoverBg: string;
  /**
   * `.crest` grounds — the colours a generated team disc is drawn on
   * (CAE-1546).
   *
   * The feed carries no crest artwork and no club colours, and §6's note says
   * to generate the discs from a two-letter abbreviation "on a solid
   * background" until real images exist. These are those backgrounds: six
   * saturated mid-darks, each clearing 4.5:1 against the white 11/700 lettering
   * they carry, so the placeholder is legible rather than merely decorative.
   *
   * A list rather than named slots, because nothing picks one by meaning — a
   * team's colour is its name hashed onto this array (`crestTeam` in
   * `pages/contests/overview/fixture-display.ts`). Reordering it reshuffles
   * every crest in the app, which is harmless but pointless; appending to it is
   * the safe way to widen the range.
   */
  crestPalette: readonly string[];
  /**
   * `.schedule-step[data-state="done"] .schedule-step-icn` — the hairline round
   * a finished milestone's green disc on the Schedule card's rail (CAE-1546).
   *
   * A shade between `successSoft` and `success`, so the disc keeps an edge
   * against the white card without the tick's own green closing up. The future
   * step's dashed edge is `toggleTrackOff`, which is already this grey.
   */
  milestoneDoneBorder: string;
  /**
   * `.preview-*` — the player-facing preview tile on the Contest Overview and
   * Edit › Basic info (CAE-1546).
   *
   * Its own ramp rather than the §1 ink scale, and the one place §7's "no
   * gradient backgrounds" rule is suspended: the tile is a *simulation of the
   * player app*, so it wears the player app's brand wash and the player app's
   * ink, not the admin's. Two of the values coincide with admin neutrals
   * today; they are kept separate because they answer to a different design.
   */
  previewTile: {
    /** §7: "a brand-flavored `#f5f3ff → #eef2ff` purple wash — intentional, only there". */
    gradient: string;
    border: string;
    super: string;
    name: string;
    sub: string;
    description: string;
  };
  /**
   * `.vw-out-thumb` — the 24px mark on a Reel Option with no artwork attached
   * (CAE-1574), holding the first letter of its pick name.
   *
   * The second place §7's "no gradient backgrounds" rule is suspended, and for
   * the narrower reason: this stands in for a *photograph* the operator has not
   * uploaded yet, so it is a placeholder for an image rather than a surface of
   * its own. A flat fill here reads as a deliberate colour choice; a wash reads
   * as a missing picture, which is what it is.
   */
  outcomeThumb: {
    gradient: string;
    text: string;
  };
  /**
   * `.prz-typepill` — the wash a Prize Tier's prize type reads in (CAE-1575).
   *
   * Keyed on the values `config/prize-tier-types.ts` offers, plus `unknown`.
   * That last one is not a spare: `prize_types` is free text in the database
   * and the option list is per-deployment config, so a row can hold a value
   * this build has no word for. It renders as the stored string in a neutral
   * wash rather than vanishing or borrowing another type's colour.
   *
   * `BET_CREDIT` takes the handoff's `CASH_REWARD` amber — the handoff draws
   * five types and this deployment configures three, and a credit is the
   * money-shaped one of the two the names do not share.
   */
  prizeTypePill: Record<
    'FREE_BET' | 'PROFIT_BOOST' | 'BET_CREDIT' | 'unknown',
    { bg: string; text: string; border: string }
  >;
  /**
   * `.prz-adapter-k` — the grey half of an adapter-config chip, which holds the
   * key while `.prz-adapter-v` holds the value on `--surface-muted`.
   *
   * A shade off `idChipKeyBg`, which does the same job for the two-tone ID
   * chip. Two near-identical greys because the stylesheet sets them
   * separately, and the kit keeps a decision per value rather than collapsing
   * the pair and picking a winner.
   */
  adapterChipKeyBg: string;
  /** `.prz-segpill` — the flat mono pill a segment group is headed with. */
  segmentPillBg: string;
  /**
   * `.prz-tierbadge` — the hairline round the accent-soft square a tier's
   * correct count sits in. The same value as the scheduled status pill's
   * border; a separate entry because they are separate decisions.
   */
  tierBadgeBorder: string;
  /**
   * `.vw-segment-head` on a group whose ref is outside the canonical
   * vocabulary — a ladder settlement can never reach (CAE-1575). The warn
   * wash, because it is a misconfiguration to fix rather than a state to read.
   */
  deadSegmentText: string;
  /**
   * The status pill's palettes said **semantically** rather than by lifecycle
   * state, for the screens outside the contest domain (CAE-1556).
   *
   * `status` above is keyed on `StatusPillState`, and deliberately so: those
   * are the states a *contest* (or, for `finished`, a fixture) can be in, and
   * `tokens.ts` goes to some length to keep anything else out of that union so
   * it cannot leak into the lifecycle mapper or the milestone rail. But an
   * admin user is Active or Inactive, a configuration key is Public or Admin
   * only, and a prize award is pending, completed or errored — none of which
   * is a lifecycle state, and all of which were drawing their own pill out of
   * `alpha(theme.palette.success.main, 0.12)` before this existed.
   *
   * Four of the five are the lifecycle palettes under their semantic names, so
   * "Active" and "Live" are the same green and the app does not grow a second
   * vocabulary of greens. `warn` is the one with no lifecycle twin — §1's ramp
   * has `warn`/`warnSoft` and the handoff pairs them everywhere a surface says
   * "this needs you" (`.cl-paused-tag`, `.m-marketsbadge-empty`,
   * `.tab-count[data-attn]`), so the pill follows that pairing. Its border is
   * `marketBadgeEmptyBorder`, which is the edge that already goes with
   * `warnSoft`; its dot is `color.warn`, as every other tone's dot is its own
   * strong value.
   */
  statePill: Record<
    'neutral' | 'success' | 'danger' | 'info' | 'warn',
    { bg: string; text: string; border: string; dot: string }
  >;
}

/** Spec §1 "Status pill colors" — explicit per-state values, not derived. */
export interface StatusPillTokens {
  bg: string;
  text: string;
  border: string;
  dot: string;
}

/**
 * The contest lifecycle states, owned by `contests/lifecycle.ts` since CAE-1541
 * and re-exported here so the palette below cannot be keyed on a different set
 * than the mapper produces. A type-only import: nothing of the domain module
 * survives into the bundle through this file.
 *
 * It is five states now, not the four §1's table draws — see the `cancelled`
 * palette below.
 */
export type LifecycleStatus = ContestLifecycleState;

/**
 * Every state the status pill can wear — the contest lifecycle states plus two
 * that belong to neither a contest nor its lifecycle.
 *
 * Kept separate from `LifecycleStatus` on purpose. The prototype's `.m-pill`
 * carries a `finished` palette and the catalogue, Add Fixtures modal and
 * Edit › Fixtures all render `<MStatusPill state={fixture.status} />` with it —
 * but a contest is never "finished", and widening `LifecycleStatus` would let it
 * leak into the lifecycle mapping and the milestone track, which are states a
 * *contest* can be in.
 *
 * - `finished` is a **fixture** that has played out. It shares draft's greys
 *   rather than closed's purple: in the design such a fixture is inert, not a
 *   contest that has settled.
 * - `feedDisconnected` is the **upstream feed** (CAE-1555), worn only by the
 *   Catalogue header. In here rather than built beside that screen so the pill
 *   has one geometry and the gallery can sign the palette off with the rest.
 */
export type StatusPillState = LifecycleStatus | 'finished' | 'feedDisconnected';

/**
 * Pill geometry, kept alongside the palettes so the status-pill component
 * (CAE-1539) inherits the density rather than re-deriving it from the handoff.
 *
 * These are `.m-pill` from `prototype/modals.css`, the pill the modals and table
 * rows render.
 *
 * > **Corrected in CAE-1539.** This block used to say §6's prose "describes the
 * > same component with different numbers (`6px 12px 6px 10px`, 12.5, gap 8, dot
 * > 7)" and was wrong to call that a disagreement. Those numbers are a real,
 * > separate rule — `.status-pill` in `prototype/contest-overview.css`, with its
 * > own palette block — which the Contest Overview header wears. The handoff
 * > draws this pill at two sizes and §6 documents the larger one. See
 * > `StatusPillSize` below; both are now in `statusPillSizes`.
 *
 * Not to be confused with the "Tab count badge" (20px tall, `0 6px`, 11px mono),
 * a different component whose numbers an earlier draft of this file had here by
 * mistake. All three are small inline-flex pills at weight 600.
 *
 * `padding` is a shorthand string, not a single `paddingX` number: the padding
 * is asymmetric, tighter on the dot side. No `height` — the pill is sized by
 * its padding and line box, as the design does it.
 */
export interface StatusPillGeometry {
  /** `.m-pill`: `3px 10px 3px 8px`. */
  padding: string;
  gap: number;
  dotSize: number;
  labelSize: string;
  labelWeight: number;
  labelTracking: string;
}

/**
 * The handoff draws this pill at two densities, and an earlier reading of it as
 * one contested size is what put the §6-versus-`modals.css` note in this file's
 * header. Both are real and both are rendered:
 *
 *   - `sm` — `.m-pill.m-pill-sm`. What `MStatusPill` produces (its own default is
 *     `size="sm"`), so it is what every screen in the bundle shows: contests
 *     table, catalogue rows, the modals, and the Edit page headers.
 *   - `md` — `.m-pill` on its own. The base class the `-sm` modifier overrides;
 *     never rendered unmodified in the bundle. Kept because CAE-1537 published it
 *     as the unqualified `--status-pill-*` variables, so dropping it would move
 *     what those names mean.
 *   - `lg` — `.status-pill` from `contest-overview.css`, a separate rule with its
 *     own palette block. Only the Contest Overview header wears it. This is the
 *     size §6's prose and CAE-1539's description both describe.
 *
 * Only padding and the label size differ; `sm` inherits `.m-pill`'s gap and dot.
 */
export type StatusPillSize = 'sm' | 'md' | 'lg';

/**
 * A type alias rather than an interface on purpose: MUI's `TypographyStyleOptions`
 * expects a string index signature, which TypeScript grants to type aliases but
 * not to interfaces.
 */
export type TypeToken = {
  fontFamily: string;
  fontSize: string;
  fontWeight: number;
  letterSpacing?: string;
  textTransform?: 'uppercase';
  textWrap?: 'balance';
  /**
   * Set only where the design specifies one. It must be explicit on any token
   * that shares a name with a built-in MUI variant (h1, h2): createTypography
   * merges ours OVER its defaults, so omitting it silently inherits MUI's
   * line-height into the theme while the CSS variable side has none — the two
   * surfaces would then render the same token at different line boxes.
   */
  lineHeight?: number;
};

export interface AdminTokens {
  color: ColorTokens;
  componentColor: ComponentColorTokens;
  status: Record<StatusPillState, StatusPillTokens>;
  statusPill: StatusPillGeometry;
  /**
   * The pill renders at three densities; `md` is `statusPill` above, repeated
   * here so a caller can index by size without special-casing the default.
   */
  statusPillSizes: Record<StatusPillSize, StatusPillGeometry>;
  radius: {
    frame: number;
    card: number;
    insetTile: number;
    pill: number;
    insetRow: number;
    idChip: number;
    /** Spec gives badges a 2–6 range: `badge` is the default, `badgeSharp` the tight end. */
    badge: number;
    badgeSharp: number;
    adapterChip: number;
    /**
     * §4's table stops at 7 (the ID chip), but the prototype draws two things at
     * 8: `.m-marketsbadge` and `.m-input-control`. Added rather than rounded to
     * the nearest listed step, because both are real measured values.
     */
    control: number;
  };
  space: { 1: number; 2: number; 3: number; 4: number; 5: number; 6: number; 8: number };
  /**
   * The side rail, from the handoff's `shared/side-nav.jsx` + the `.sn-*` rules
   * in `shared/modals.css` — the runnable prototype rather than §6's prose,
   * which describes only the collapsed state (see theme/README.md).
   *
   * Two widths, because the rail has two forms: 240px with labels and an
   * identity block, and 64px icon-only below the design's 1024px breakpoint.
   */
  navRail: {
    widthExpanded: number;
    widthCollapsed: number;
    /** The viewport width at or below which the rail is always collapsed. */
    collapseBelow: number;
    /**
     * Kept as numbers rather than one `padding` shorthand: the rail composes
     * its own shorthand per form, so expanded and collapsed can differ without
     * either restating the other's value.
     */
    railPaddingY: number;
    railPaddingX: number;
    railPaddingYCollapsed: number;
    railPaddingXCollapsed: number;
    itemPadding: string;
    itemGap: number;
    iconSize: number;
    labelSize: string;
    labelWeight: number;
    labelTracking: string;
    /** The active destination reads heavier as well as filled. */
    activeWeight: number;
    avatarSize: number;
    identityRadius: number;
    identityPadding: string;
    identityPaddingCollapsed: string;
    nameSize: string;
    roleSize: string;
  };
  /** Spec §3 — the fixed measurements the shell and cards are built from. */
  layout: {
    pageMaxWidth: number;
    /**
     * The wider container the data-table screens use. `.cl-page` is
     * `max-width: 1440px` where `.cat-page` and `.rl-page` are 1240 — a list
     * with eight columns needs the room, and at 1240 it is squeezed into the
     * middle of the viewport with gutters either side.
     */
    pageMaxWidthWide: number;
    pagePaddingX: number;
    pagePaddingTop: number;
    pagePaddingBottom: number;
    frameCardPadding: number;
    cardPadding: number;
    cardGap: number;
    cardInnerGap: number;
    /** `.tabs { margin-bottom: 18px }` — the segmented strip to its panel. */
    tabStripGap: number;
  };
  /**
   * §6 "Buttons" — the pill every action renders as, in three variants.
   *
   * Geometry follows §6's prose (`9px 18px`, 14/500), which is the same choice
   * CAE-1537 made for `typography.button`; the handoff's `shared/modals.css`
   * draws `.m-btn` at `9px 16px` / 13.5px instead. Keeping the two in step
   * matters more than either number, and theme/README.md tracks the divergence.
   */
  button: {
    padding: string;
    /** Outline's border — a blue-tinted line, not the neutral `--line`. */
    outlineBorder: string;
    outlineBorderHover: string;
    /** Primary's hover fill: §6 "darken to #1d4fd1". */
    primaryHover: string;
  };
  border: { width: string; default: string };
  shadow: {
    card: string;
    tabSelected: string;
    currentStepRing: string;
    /** `.m-toggle-thumb` — lifts the thumb off its track. */
    toggleThumb: string;
    /** `.m-popover` — a floating panel, heavier than a card's whisper. */
    popover: string;
    /**
     * `--shadow-modal` in modals.css's `:root`. The README's 08-modals prose
     * gives a different, heavier pair; the stylesheet is what was signed off.
     */
    modal: string;
  };
  font: { sans: string; mono: string; featureSettings: string };
  text: Record<
    | 'h1'
    | 'h2'
    | 'featuredEvent'
    | 'kpiValue'
    | 'cardTitle'
    | 'bodyLabel'
    | 'tab'
    | 'bodySecondary'
    | 'meta'
    | 'eyebrow'
    | 'counterBadge',
    TypeToken
  >;
  /** Tabs render at weight 500 and switch to this when selected (see theme/README.md). */
  tabSelectedWeight: number;
}

const color: ColorTokens = {
  bg: '#f3f4f7',
  surface: '#ffffff',
  surfaceMuted: '#fafbfc',
  line: '#ececf0',
  lineStrong: '#dfe0e5',
  ink: '#0c0d10',
  ink2: '#2c2e36',
  ink3: INK_3,
  ink4: '#9aa0ab',
  accent: ACCENT,
  accentSoft: ACCENT_SOFT,
  accentStrong: ACCENT_STRONG,
  success: '#1f9d55',
  successSoft: '#e6f4ec',
  warn: '#c2620a',
  warnSoft: '#fdefdc',
  danger: '#c0392b',
  dangerSoft: '#fce8e6',
};

/**
 * The four pill palettes that have both a lifecycle name and a semantic one.
 *
 * `status` below keys them by the state a contest is in; `componentColor.
 * statePill` keys the same objects by what they *mean*, for the screens
 * outside the contest domain (CAE-1556). One object each rather than two
 * literals, so an admin user's "Active" and a contest's "Live" cannot drift
 * into two different greens.
 */
const PILL_NEUTRAL = {
  bg: NEUTRAL_SOFT,
  text: INK_ON_NEUTRAL_SOFT,
  border: '#dfe1e7',
  dot: '#7a7f8a',
} as const;

const PILL_ACCENT = {
  // The prototype sets this to `var(--accent-soft)`, not a literal — keep the
  // relationship so the two cannot drift apart.
  bg: color.accentSoft,
  text: ACCENT_STRONG,
  border: '#c7d4f5',
  dot: '#3b82f6',
} as const;

const PILL_SUCCESS = {
  bg: color.successSoft,
  text: SUCCESS_STRONG,
  border: '#bfe3cc',
  dot: '#1f9d55',
} as const;

/**
 * The values are the Over/Under admin's `canceled` pill, the sibling product
 * the same operators use, so the colour means the same thing in both.
 */
const PILL_DANGER = {
  bg: '#faeae8',
  text: '#8f2b1f',
  border: '#f0cfc9',
  dot: '#c0392b',
} as const;

export const adminTokens: AdminTokens = {
  color,
  componentColor: {
    idChipKeyBg: '#f4f5f8',
    metaSeparator: '#cdd0d8',
    outcomeSelectedInset: '#c7d4f5',
    dangerSoftBorder: '#f3c7c0',
    dangerSoftBorderHover: '#e9a59c',
    ringTrack: TRACK_NEUTRAL,
    rowHover: '#f7f8fa',
    readyBannerText: '#0f7a3f',
    barTrack: NEUTRAL_SOFT,
    marketBadgeOkText: SUCCESS_STRONG,
    marketBadgeOkSubText: '#5a8a6e',
    marketBadgeEmptyText: WARN_STRONG,
    marketBadgeEmptyBorder: '#f5d8b0',
    countBadgeBg: '#dadce2',
    countBadgeText: '#5a5e69',
    countBadgeSelectedBg: NEUTRAL_SOFT,
    countBadgeSelectedText: INK_ON_NEUTRAL_SOFT,
    countBadgeAttnBg: '#fce0c5',
    countBadgeAttnText: WARN_STRONG,
    setupRailIdleRing: '#c5c8cf',
    toggleTrackOff: MUTED_EDGE,
    fixtureRowHover: '#f3f5f8',
    viewRowHoverBorder: '#cdd0d8',
    emptyArtBg: '#f5f6f8',
    modalScrim: 'rgba(12,13,16,.40)',
    dotSeparator: '#cdd0d8',
    fixtureBadge: {
      upcoming: { bg: ACCENT_SOFT, text: '#1a4dc4' },
      live: { bg: '#e6f4ec', text: '#0f7a3f' },
      finished: { bg: NEUTRAL_SOFT, text: '#4a4f5b' },
    },
    statusChipActiveCountBg: 'rgba(255,255,255,.15)',
    statusChipCountBg: NEUTRAL_SOFT,
    statusChipCountText: INK_3,
    statusChipHoverBorder: '#cdd0d8',
    controlHoverBorder: '#c5c8cf',
    listIdPillBg: '#f4f5f8',
    rowResumeHoverBg: '#d6ecdf',
    crestPalette: ['#1f4eb6', '#b3322c', '#1f7a4d', '#7a3fb8', '#8a5612', '#1f6f8f'],
    milestoneDoneBorder: '#bfe3cc',
    outcomeThumb: {
      gradient: 'linear-gradient(135deg, #1f8a5b, #0f5a3a)',
      text: '#ffffff',
    },
    adapterChipKeyBg: '#f3f4f7',
    segmentPillBg: '#f4f5f8',
    tierBadgeBorder: '#c7d4f5',
    deadSegmentText: WARN_STRONG,
    statePill: {
      neutral: PILL_NEUTRAL,
      success: PILL_SUCCESS,
      danger: PILL_DANGER,
      info: PILL_ACCENT,
      // The one tone with no lifecycle twin. `marketBadgeEmptyBorder` is the
      // edge that already pairs with `warnSoft` in the market badge, and
      // `WARN_STRONG` is the label weight §1's `--warn` is too light for.
      warn: {
        bg: color.warnSoft,
        text: WARN_STRONG,
        border: '#f5d8b0',
        dot: color.warn,
      },
    },
    prizeTypePill: {
      FREE_BET: { bg: color.successSoft, text: SUCCESS_STRONG, border: '#bfe3cc' },
      PROFIT_BOOST: { bg: '#f0e9ff', text: '#5b3fc2', border: '#d8ccfa' },
      BET_CREDIT: { bg: '#fdf3e3', text: '#8a4a08', border: '#f5d8b0' },
      unknown: { bg: NEUTRAL_SOFT, text: INK_ON_NEUTRAL_SOFT, border: '#dfe1e7' },
    },
    previewTile: {
      gradient: 'linear-gradient(180deg, #f5f3ff 0%, #eef2ff 100%)',
      border: '#dfe2f5',
      super: '#5b3fc2',
      name: '#1a1c25',
      sub: '#3a3d46',
      description: '#5a5e69',
    },
  },
  status: {
    draft: PILL_NEUTRAL,
    scheduled: PILL_ACCENT,
    live: PILL_SUCCESS,
    closed: {
      bg: '#efeaff',
      text: '#4a2eb0',
      border: '#d8ccfa',
      dot: '#7a5bd9',
    },
    /*
     * The fifth lifecycle state, added by CAE-1541.
     *
     * §1's table stops at four because the handoff has no cancelled contest in
     * it — but the backend does, and the decision on that ticket was a pill of
     * its own rather than `closed` plus a tag.
     */
    cancelled: PILL_DANGER,
    // `.m-pill-finished` — a fixture that has played out, not a settled contest.
    // The prototype gives it draft's greys verbatim rather than its own palette.
    finished: PILL_NEUTRAL,
    /*
     * `.cat-feedoff-pill` in `prototype/setup-rail.css` — the Catalogue header
     * when the upstream feed is unreachable (CAE-1555).
     *
     * **Amber, not the slate CAE-1555's description asks for.** The stylesheet
     * paints it `--sr-attn` on `--sr-attn-bg`, which are `warn` and `warnSoft`
     * here to the byte, and the stylesheet has won every one of these through
     * the epic. Slate would also say the wrong thing: `draft` is the colour of
     * something inert and expected, and a dead feed is neither.
     *
     * The border is the one value with no token behind it. `#f4cf9a` appears
     * exactly once in the whole bundle, on this rule, one step darker than the
     * `#f5d8b0` that six other amber edges share as `marketBadgeEmptyBorder`.
     * Carried as its own rather than rounded onto that one.
     */
    feedDisconnected: {
      bg: color.warnSoft,
      text: color.warn,
      border: '#f4cf9a',
      dot: color.warn,
    },
  },
  statusPill: STATUS_PILL_MD,
  statusPillSizes: {
    // `.m-pill-sm` overrides only these two; gap and dot come from `.m-pill`.
    sm: { ...STATUS_PILL_MD, padding: '2px 8px 2px 7px', labelSize: '11.5px' },
    md: STATUS_PILL_MD,
    // `.status-pill` in contest-overview.css — its own rule, not a modifier.
    lg: {
      padding: '6px 12px 6px 10px',
      gap: 8,
      dotSize: 7,
      labelSize: '12.5px',
      labelWeight: 600,
      labelTracking: '0.01em',
    },
  },
  radius: {
    frame: 18,
    card: 14,
    insetTile: 12,
    pill: 999,
    insetRow: 10,
    idChip: 7,
    badge: 6,
    badgeSharp: 2,
    /** `.prz-adapter-kv` — tighter than `badge`, and a measured value like `control`. */
    adapterChip: 5,
    control: 8,
  },
  space: { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 8: 32 },
  navRail: {
    widthExpanded: 240,
    widthCollapsed: 64,
    collapseBelow: 1024,
    railPaddingY: 16,
    railPaddingX: 12,
    railPaddingYCollapsed: 14,
    railPaddingXCollapsed: 10,
    itemPadding: '9px 12px',
    itemGap: 10,
    iconSize: 18,
    labelSize: '13.5px',
    labelWeight: 500,
    labelTracking: '-0.005em',
    activeWeight: 600,
    avatarSize: 34,
    identityRadius: 12,
    identityPadding: '10px 12px',
    identityPaddingCollapsed: '8px',
    nameSize: '13.5px',
    roleSize: '11.5px',
  },
  layout: {
    pageMaxWidth: 1240,
    pageMaxWidthWide: 1440,
    pagePaddingX: 32,
    pagePaddingTop: 32,
    pagePaddingBottom: 64,
    // The spec's density tweak moves these (frame 22/28/32, card 18/22/26);
    // the comfortable middle is the default the screens are drawn at.
    frameCardPadding: 28,
    cardPadding: 22,
    cardGap: 20,
    cardInnerGap: 16,
    tabStripGap: 18,
  },
  button: {
    padding: '9px 18px',
    outlineBorder: '#cdd9f5',
    outlineBorderHover: '#b5c5f0',
    primaryHover: '#1d4fd1',
  },
  border: {
    // 0.5px is deliberate — 1px reads too heavy at this density.
    width: '0.5px',
    // var(), not the literal: a scoped re-skin overriding --line must move both
    // the CSS and the sx surface together.
    default: '0.5px solid var(--line)',
  },
  shadow: {
    card: '0 1px 0 rgba(16,17,22,.02), 0 1px 2px rgba(16,17,22,.04)',
    tabSelected: '0 1px 2px rgba(16,17,22,.06), 0 0 0 .5px rgba(16,17,22,.05)',
    currentStepRing: '0 0 0 4px var(--accent-soft)',
    toggleThumb: '0 1px 2px rgba(16,17,22,.18)',
    popover: '0 16px 40px -10px rgba(16,17,22,.20), 0 4px 14px -4px rgba(16,17,22,.08)',
    modal: '0 24px 60px -20px rgba(16,17,22,.18), 0 8px 24px -12px rgba(16,17,22,.12)',
  },
  font: {
    sans: SANS_STACK,
    mono: MONO_STACK,
    featureSettings: "'ss01', 'cv11'",
  },
  text: {
    h1: {
      fontFamily: SANS_STACK,
      fontSize: '36px',
      fontWeight: 600,
      letterSpacing: '-0.025em',
      textWrap: 'balance',
      // 1.1 from the handoff's `modals.css`; §2 specifies no line-height, and
      // one must be explicit here or MUI's h1 default leaks in (see TypeToken).
      lineHeight: 1.1,
    },
    h2: {
      fontFamily: SANS_STACK,
      fontSize: '22px',
      fontWeight: 700,
      letterSpacing: '-0.015em',
      // 1.2 from `modals.css`, same reasoning as h1.
      lineHeight: 1.2,
    },
    featuredEvent: {
      fontFamily: SANS_STACK,
      fontSize: '22px',
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    kpiValue: {
      fontFamily: MONO_STACK,
      fontSize: '28px',
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    cardTitle: {
      fontFamily: SANS_STACK,
      fontSize: '15px',
      fontWeight: 600,
      letterSpacing: '-0.005em',
    },
    bodyLabel: { fontFamily: SANS_STACK, fontSize: '14px', fontWeight: 500 },
    tab: { fontFamily: SANS_STACK, fontSize: '14px', fontWeight: 500 },
    bodySecondary: { fontFamily: SANS_STACK, fontSize: '13px', fontWeight: 400 },
    meta: { fontFamily: SANS_STACK, fontSize: '12.5px', fontWeight: 400 },
    eyebrow: {
      fontFamily: SANS_STACK,
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    counterBadge: { fontFamily: MONO_STACK, fontSize: '11px', fontWeight: 600 },
  },
  tabSelectedWeight: 600,
};
